/**
 * EventEmitter.js - Simple Event Emitter
 * --------------------------------------
 * Lightweight event system for internal communication between modules.
 * Used throughout the contact system to broadcast and listen for key events
 * (e.g., modal opened, modal failed, hydration complete, etc.).
 * Now includes essential error tracking for critical event system failures.
 *
 * Responsibilities:
 * - Register event listeners (`on`)
 * - Remove specific listeners (`off`)
 * - Emit events with optional payloads (`emit`)
 * - Handle listener execution with error isolation
 * - Support teardown via `destroy()`
 * - Essential error tracking for critical event system failures
 *
 * Features:
 * - Stores listeners in a Map keyed by event type
 * - Ensures event isolation (errors in one listener do not affect others)
 * - Supports multiple listeners per event type
 * - Provides graceful cleanup of all event bindings
 * - Minimal error tracking for essential event system failures
 *
 * Limitations:
 * - Does not distinguish between sync vs. async handlers
 * - Error tracking is minimal to avoid code explosion (only critical failures)
 * - Error data is stored in memory (non-persistent across page reloads)
 */

import ErrorHandler from '../../utils/ErrorHandler.js';

const isServerSide = typeof window === 'undefined';
const isClientSide = !isServerSide;

class EventEmitter {
  // Shared error handler
  static _errorHandler = null;

  static _initErrorHandler() {
    if (this._errorHandler) return;
    try {
      this._errorHandler = new ErrorHandler({ module: 'EventEmitter', maxErrors: 20 });
    } catch {
      this._errorHandler = {
        handleError:       () => {},
        handleFatalError:  () => {},
        getRecentErrors:   () => [],
        hasFatalErrors:    () => false,
        getErrorStats:     () => ({ totalErrors: 0, fatalErrors: 0 })
      };
    }
  }

  constructor(options = {}) {
    EventEmitter._initErrorHandler();
    this._errorHandler = EventEmitter._errorHandler;

    this.eventListeners = new Map();
    this.instanceId = this._generateInstanceId();
    this.createdAt = new Date().toISOString();
    this.options = {
      maxListeners: options.maxListeners || 50,
      enableErrorTracking: options.enableErrorTracking !== false,
      ...options
    };
  }

  _generateInstanceId() {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
  }

  on(eventType, listener) {
    try {
      if (typeof eventType !== 'string' || !eventType) {
        throw new Error('Event type must be a non-empty string');
      }
      const listeners = this.eventListeners.get(eventType) || [];
      if (listeners.length >= this.options.maxListeners) {
        throw new Error(`Max listeners reached for event: ${eventType}`);
      }
      listeners.push(listener);
      this.eventListeners.set(eventType, listeners);
    } catch (err) {
      this._errorHandler.handleError('on() failed', err, { eventType });
    }
  }

  off(eventType, listener) {
    try {
      const listeners = this.eventListeners.get(eventType) || [];
      const idx = listeners.indexOf(listener);
      if (idx > -1) {
        listeners.splice(idx, 1);
        this.eventListeners.set(eventType, listeners);
      }
    } catch (err) {
      this._errorHandler.handleError('off() failed', err, { eventType });
    }
  }

  emit(eventType, payload) {
    try {
      const listeners = this.eventListeners.get(eventType) || [];
      for (const listener of listeners.slice()) {
        try {
          listener(payload);
        } catch (listenerErr) {
          this._errorHandler.handleError(
            `Listener error on event: ${eventType}`,
            listenerErr,
            { eventType }
          );
        }
      }
    } catch (err) {
      this._errorHandler.handleFatalError('emit() failed', err, { eventType });
    }
  }

  destroy() {
    try {
      const eventTypeCount = this.eventListeners.size;
      let listenerCount = 0;
      for (const listeners of this.eventListeners.values()) {
        listenerCount += listeners.length;
      }
      this.eventListeners.clear();
      console.log(
        `EventEmitter: Destroyed instance ${this.instanceId} (${listenerCount} listeners across ${eventTypeCount} event types)`
      );
    } catch (error) {
      this._errorHandler.handleError('destroy() failed', error, {
        category: 'listener-management',
        operation: 'destroy'
      });
      console.error('EventEmitter: destroy failed:', error);
    }
  }
}

export default EventEmitter;
